// Mirrored in applications/platform-app/components/web-analytics — change both.
import React from 'react';
import { useDash } from '../context';
import { BreakdownCard, useBreakdown, useFunnel } from './shared';
import { Card, Empty, ErrorLine, Muted, Note, Td, Th, rowButton } from '../ui';
import { fmt, moneyCents, RUBY, valueLabel } from '../format';

const Funnel: React.FC = () => {
  const d = useDash();
  const company = d.siteKind === 'company';
  const f = useFunnel();
  const steps = f.data?.steps || [];
  const first = steps[0]?.count || 0;
  return (
    <Card title={<>{company ? 'Sign-up funnel' : 'Shopping funnel'} <Muted>· share of visits reaching each step</Muted></>} testId="wa-funnel">
      {f.error ? <ErrorLine message={f.error} /> : !first ? <Empty /> : steps.map((s, i) => {
        const frac = first ? s.count / first : 0;
        const prev = i ? steps[i - 1].count : 0;
        return (
          <div key={s.name} className="grid gap-3 items-center mb-2.5" style={{ gridTemplateColumns: 'minmax(120px,170px) 1fr 90px' }}>
            <span><b>{s.label}</b><div className="text-stone-500 text-[11px]">{(frac * 100).toFixed(1)}% of visits</div></span>
            <div>
              <div className="h-[34px] rounded-lg flex items-center px-2.5 text-white font-semibold text-xs min-w-[56px]" style={{ width: `${Math.max(6, frac * 100)}%`, background: RUBY, opacity: 1 - i * 0.12 }}>
                {fmt(s.count)}
              </div>
            </div>
            <span className="text-[11px] text-right" style={{ color: '#9F1239' }}>{i && prev ? `−${Math.max(0, (1 - s.count / prev) * 100).toFixed(0)}% drop` : ''}</span>
          </div>
        );
      })}
    </Card>
  );
};

const Products: React.FC = () => {
  const d = useDash();
  const q = useBreakdown('product', 10);
  const rows = q.data?.rows || [];
  return (
    <Card title="Most viewed vs bought">
      {q.error ? <ErrorLine message={q.error} /> : !rows.length ? <Empty /> : (
        <table className="w-full border-collapse text-[13px]">
          <thead><tr><Th>Product</Th><Th num>Views</Th><Th num>Bought</Th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const click = d.rowFilter('product', r.value);
              return (
                <tr key={r.value} {...rowButton(click, `Filter by product ${r.value}`)}>
                  <Td className="max-w-[180px] truncate">{valueLabel('product', r.value)}</Td>
                  <Td num>{fmt(r.hits || r.pv)}</Td>
                  <Td num>{fmt(r.orders)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
};

export const ConversionsTab: React.FC = () => {
  const d = useDash();
  const company = d.siteKind === 'company';
  return (
    <div>
      <Funnel />
      <div className="grid gap-3.5 mt-3.5 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {company
          ? <BreakdownCard title="Sign-ups by channel" dim="channel" value={(r) => r.signups} />
          : <BreakdownCard title="Revenue by channel" dim="channel" value={(r) => r.revenueCents} format={moneyCents} />}
        {company ? <BreakdownCard title="Sign-ups by source" dim="source" value={(r) => r.signups} /> : <Products />}
        <BreakdownCard title="New vs returning" dim="visitorType" donut />
      </div>
      <Note>
        {company
          ? 'Sign-ups are counted when an account is actually created, not when a button is clicked.'
          : 'Orders and revenue are confirmed paid online orders, matched to the visit that produced them. They are not taken from clicks in the browser.'}
      </Note>
    </div>
  );
};

export default ConversionsTab;
